import { z } from 'zod';
import { postCategorySchema, postTypeSchema } from './enums.js';
// --- Errors ----------------------------------------------------------------
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        details: z.unknown().optional(),
    }),
});
export const API_ERROR_CODES = {
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    VALIDATION: 'VALIDATION',
    CONFLICT: 'CONFLICT',
    RATE_LIMITED: 'RATE_LIMITED',
    UPSTREAM: 'UPSTREAM',
    INTERNAL: 'INTERNAL',
};
// --- Query param coercion helpers ------------------------------------------
// Everything arrives as a string in a query string, so these normalise before
// validating rather than relying on z.coerce (which turns "false" into true).
const csv = (item) => z.preprocess((value) => {
    if (typeof value === 'string')
        return value.split(',').filter(Boolean);
    return value;
}, z.array(item));
const queryBoolean = z.preprocess((value) => {
    if (value === 'true' || value === '1')
        return true;
    if (value === 'false' || value === '0')
        return false;
    return value;
}, z.boolean());
// --- Pagination ------------------------------------------------------------
export const DEFAULT_PAGE_SIZE = 40;
export const MAX_PAGE_SIZE = 100;
export const pageSchema = z.object({
    /** Opaque keyset cursor. Pass it back verbatim to get the next page. */
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
});
export function listResponseSchema(item) {
    return z.object({
        items: z.array(item),
        page: pageSchema,
        total: z.number().int().nullable(),
    });
}
// --- Posts -----------------------------------------------------------------
export const postSortSchema = z.enum(['newest', 'oldest', 'relevance']);
export const postListQuerySchema = z.object({
    /** Full-text search over post text. Required for `sort=relevance`. */
    q: z.string().trim().min(1).max(300).optional(),
    targetIds: csv(z.string()).optional(),
    types: csv(postTypeSchema).optional(),
    tagIds: csv(z.string()).optional(),
    hasMedia: queryBoolean.optional(),
    isPinned: queryBoolean.optional(),
    isRead: queryBoolean.optional(),
    /**
     * Omitted includes both. The live feed passes false to stay clean; the
     * archive leaves it unset so nothing silently vanishes from the record.
     */
    isDeleted: queryBoolean.optional(),
    categories: csv(postCategorySchema).optional(),
    minImportance: z.coerce.number().int().min(0).max(100).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    sort: postSortSchema.default('newest'),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: z.string().optional(),
});
/** Replay for SSE reconnection: everything strictly after this cursor. */
export const postSinceQuerySchema = z.object({
    seq: z.coerce.number().int().min(0),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(MAX_PAGE_SIZE),
});
export const updatePostRequestSchema = z.object({
    isRead: z.boolean().optional(),
    isPinned: z.boolean().optional(),
    note: z.string().max(4000).nullish(),
    tagIds: z.array(z.string()).optional(),
});
export const bulkUpdatePostsRequestSchema = z.object({
    ids: z.array(z.string()).min(1).max(500),
    isRead: z.boolean().optional(),
    isPinned: z.boolean().optional(),
});
// --- Tags ------------------------------------------------------------------
export const createTagRequestSchema = z.object({
    name: z.string().trim().min(1).max(40),
    color: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .optional(),
});
//# sourceMappingURL=api.js.map